import { isBrowser } from './Utils';

/**
 * Deployment environments for SpectrumReach.com.
 *
 * Mirrors the pipeline's real environments (dev → stage → prod) plus `local`
 * for developer machines. `Local` is also the fallback when nothing resolves.
 */
export enum AppEnv {
    Local = `local`,
    Dev = `dev`,
    Stage = `stage`,
    Prod = `prod`,
}

const APP_ENV_VALUES = Object.values(AppEnv) as string[];

/** Hosts that serve production traffic (see `.deploy/content-prod.yml`). */
const PROD_HOSTS = [
    `spectrumreach.com`,
    `www.spectrumreach.com`,
    `spectrumreach.spectrumflow.net`,
];

const LOCAL_HOSTS = [`localhost`, `127.0.0.1`, `0.0.0.0`];

/**
 * Normalize a `GATSBY_DD_RUM_ENV` value to an AppEnv.
 *
 * CI sets values like `dev-reach` / `prod-reach` (and `local` for local runs),
 * so the `-reach` suffix is stripped before matching. Returns `undefined` for
 * empty or unrecognized values so the caller can fall through.
 */
export const parseAppEnv = (value?: string | null): AppEnv | undefined => {
    if (!value) return undefined;

    const normalized = value.trim().toLowerCase().replace(/-reach$/, ``);

    return APP_ENV_VALUES.includes(normalized)
        ? (normalized as AppEnv)
        : undefined;
};

/**
 * Host-based App_Env detection, used only when no build-time value is present.
 * Returns `undefined` for hosts it does not recognize.
 */
export const getAppEnvByHost = (hostname?: string | null): AppEnv | undefined => {
    if (!hostname) return undefined;

    const host = hostname.toLowerCase();

    if (LOCAL_HOSTS.includes(host)) return AppEnv.Local;
    if (PROD_HOSTS.includes(host)) return AppEnv.Prod;
    if (host.startsWith(`dev-`) || host.startsWith(`dev.`)) return AppEnv.Dev;
    if (host.startsWith(`stage-`) || host.startsWith(`stage.`)) return AppEnv.Stage;

    return undefined;
};

/**
 * Resolve the current App_Env.
 *
 * Order:
 *   1. Build-time `GATSBY_DD_RUM_ENV` (inlined by Gatsby at build).
 *   2. Host-based detection from `window.location.hostname` (browser only).
 *   3. `AppEnv.Local` (most restrictive default).
 *
 * Reads `window` only inside the call and only behind `isBrowser`, so it is
 * safe to import during SSG.
 */
export const getAppEnv = (): AppEnv => {
    // Must be referenced literally so Gatsby's DefinePlugin can inline it.
    const fromBuild = parseAppEnv(process.env.GATSBY_DD_RUM_ENV);
    if (fromBuild) return fromBuild;

    if (isBrowser) {
        const fromHost = getAppEnvByHost(window.location.hostname);
        if (fromHost) return fromHost;
    }

    return AppEnv.Local;
};

/** Whether the resolved (or provided) App_Env is production. */
export const isProd = (appEnv: AppEnv = getAppEnv()): boolean =>
    appEnv === AppEnv.Prod;
